import {
    ActionIcon,
    Box,
    Group,
    Loader,
    Modal,
    Text,
    Tooltip,
    UnstyledButton,
    useMantineColorScheme,
} from "@mantine/core";
import { IconPhotoOff, IconZoomIn, IconZoomOut, IconZoomReset } from "@tabler/icons-react";
import { type CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import type { Reference } from "../db";
import { useArtifactHandler } from "../utils/artifactHandler";

export const IMAGE_DATASOURCE_TYPE = "image";

const MIN_SCALE = 0.25;
const MAX_SCALE = 8;
const BUTTON_ZOOM_FACTOR = 1.25;
const WHEEL_ZOOM_SENSITIVITY = 0.0015;

/**
 * Image references carry `base64_image` only on the live stream; conversation history drops it,
 * so the type alone decides and the picture is re-fetched from `source` when needed.
 */
export const isImageReference = (reference: Reference): boolean =>
    reference.dataSourceType === IMAGE_DATASOURCE_TYPE;

const getInlineImageSrc = (reference: Reference): string | null => {
    const data = reference.base64Image;
    if (!data) return null;
    // Some producers already send a full data URI.
    if (data.startsWith("data:")) return data;
    return `data:${reference.mimeType || "image/jpeg"};base64,${data}`;
};

// Without an S3 key the backend falls back to `source = reference_id`, which can't be fetched.
const canLoadFromSource = (source: string) =>
    source.includes("v1/storage/") || source.startsWith("http");

// Object URLs keyed by `source`, shared by thumbnails, hover cards and the viewer so each image
// is downloaded once per page load.
const storageImageCache = new Map<string, Promise<string | null>>();

export const useImageSrc = (reference: Reference | null) => {
    const { getArtifactBlob } = useArtifactHandler();
    const inlineSrc = reference ? getInlineImageSrc(reference) : null;
    const source = reference?.source ?? "";
    const shouldFetch = !inlineSrc && canLoadFromSource(source);
    const [loaded, setLoaded] = useState<{ source: string; src: string | null } | null>(null);

    useEffect(() => {
        if (!shouldFetch) return;
        let cancelled = false;
        let pending = storageImageCache.get(source);
        if (!pending) {
            pending = getArtifactBlob(source).then((blob) =>
                blob ? URL.createObjectURL(blob) : null,
            );
            storageImageCache.set(source, pending);
        }
        pending.then((src) => {
            // Let a later render retry instead of caching the failure.
            if (!src) storageImageCache.delete(source);
            if (!cancelled) setLoaded({ source, src });
        });
        return () => {
            cancelled = true;
        };
    }, [getArtifactBlob, shouldFetch, source]);

    if (inlineSrc) return { src: inlineSrc, loading: false };
    if (!shouldFetch) return { src: null, loading: false };
    if (loaded?.source !== source) return { src: null, loading: true };
    return { src: loaded.src, loading: false };
};

const getImageFormatLabel = (reference: Reference): string => {
    const fromMime = reference.mimeType?.split("/")[1];
    if (fromMime) return fromMime.toUpperCase();
    const extension = /\.([a-z0-9]{2,5})$/i.exec(reference.source)?.[1];
    return (extension || "image").toUpperCase();
};

const ReferenceImage = ({
    reference,
    style,
    lazy = false,
}: {
    reference: Reference;
    style?: CSSProperties;
    lazy?: boolean;
}) => {
    const { src, loading } = useImageSrc(reference);

    if (loading) return <Loader size="sm" data-testid="image-reference-loading" />;
    if (!src) {
        return (
            <Box ta="center" c="dimmed" data-testid="image-reference-unavailable">
                <IconPhotoOff size={28} aria-hidden />
                <Text size="xs">Image unavailable</Text>
            </Box>
        );
    }
    return (
        <img
            src={src}
            alt={reference.title || "Reference image"}
            loading={lazy ? "lazy" : undefined}
            draggable={false}
            style={style}
        />
    );
};

const clampScale = (scale: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));

interface ViewTransform {
    scale: number;
    x: number;
    y: number;
}

const IDENTITY: ViewTransform = { scale: 1, x: 0, y: 0 };

/**
 * Zoom keeping the point under (px, py) fixed. Coordinates are relative to the viewport centre,
 * which is the image's transform origin.
 */
const zoomAt = (view: ViewTransform, nextScale: number, px: number, py: number) => {
    const scale = clampScale(nextScale);
    const ratio = scale / view.scale;
    return {
        scale,
        x: px - (px - view.x) * ratio,
        y: py - (py - view.y) * ratio,
    };
};

export const ImageViewerModal = ({
    reference,
    opened,
    onClose,
}: {
    reference: Reference | null;
    opened: boolean;
    onClose: () => void;
}) => {
    const { colorScheme } = useMantineColorScheme();
    const [view, setView] = useState<ViewTransform>(IDENTITY);
    const [viewport, setViewport] = useState<HTMLDivElement | null>(null);
    const dragRef = useRef<{ pointerId: number; startX: number; startY: number } | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    // Reset whenever a different image is opened.
    // biome-ignore lint/correctness/useExhaustiveDependencies: reset is keyed on the reference
    useEffect(() => {
        if (opened) setView(IDENTITY);
    }, [reference?.reference_id, opened]);

    // React's onWheel is passive, so preventDefault (to stop the modal scrolling) needs a
    // native listener.
    useEffect(() => {
        if (!viewport) return;
        const handleWheel = (event: WheelEvent) => {
            event.preventDefault();
            const rect = viewport.getBoundingClientRect();
            const px = event.clientX - rect.left - rect.width / 2;
            const py = event.clientY - rect.top - rect.height / 2;
            const factor = Math.exp(-event.deltaY * WHEEL_ZOOM_SENSITIVITY);
            setView((current) => zoomAt(current, current.scale * factor, px, py));
        };
        viewport.addEventListener("wheel", handleWheel, { passive: false });
        return () => viewport.removeEventListener("wheel", handleWheel);
    }, [viewport]);

    const zoomBy = useCallback((factor: number) => {
        setView((current) => zoomAt(current, current.scale * factor, 0, 0));
    }, []);

    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        if (event.button !== 0) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        dragRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX - view.x,
            startY: event.clientY - view.y,
        };
        setIsDragging(true);
    };

    const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        const x = event.clientX - drag.startX;
        const y = event.clientY - drag.startY;
        setView((current) => ({ ...current, x, y }));
    };

    const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
        if (dragRef.current?.pointerId !== event.pointerId) return;
        dragRef.current = null;
        setIsDragging(false);
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            size="90%"
            centered
            data-testid="image-viewer-modal"
            title={
                <Text fw={500} lineClamp={2} data-testid="image-viewer-title">
                    {reference?.title || "Image"}
                </Text>
            }
        >
            {reference && (
                <>
                    <Group justify="space-between" mb="xs">
                        <Text size="xs" c="dimmed">
                            Scroll to zoom · drag to pan · double-click to reset
                        </Text>
                        <Group gap={4}>
                            <Tooltip label="Zoom out">
                                <ActionIcon
                                    variant="default"
                                    aria-label="Zoom out"
                                    data-testid="image-viewer-zoom-out"
                                    disabled={view.scale <= MIN_SCALE}
                                    onClick={() => zoomBy(1 / BUTTON_ZOOM_FACTOR)}
                                >
                                    <IconZoomOut size={16} />
                                </ActionIcon>
                            </Tooltip>
                            <Text
                                size="xs"
                                w={48}
                                ta="center"
                                data-testid="image-viewer-zoom-level"
                            >
                                {Math.round(view.scale * 100)}%
                            </Text>
                            <Tooltip label="Zoom in">
                                <ActionIcon
                                    variant="default"
                                    aria-label="Zoom in"
                                    data-testid="image-viewer-zoom-in"
                                    disabled={view.scale >= MAX_SCALE}
                                    onClick={() => zoomBy(BUTTON_ZOOM_FACTOR)}
                                >
                                    <IconZoomIn size={16} />
                                </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Reset view">
                                <ActionIcon
                                    variant="default"
                                    aria-label="Reset view"
                                    data-testid="image-viewer-reset"
                                    onClick={() => setView(IDENTITY)}
                                >
                                    <IconZoomReset size={16} />
                                </ActionIcon>
                            </Tooltip>
                        </Group>
                    </Group>
                    <Box
                        ref={setViewport}
                        data-testid="image-viewer-viewport"
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={endDrag}
                        onPointerCancel={endDrag}
                        onDoubleClick={() => setView(IDENTITY)}
                        style={(theme) => ({
                            position: "relative",
                            height: "70vh",
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: theme.radius.sm,
                            backgroundColor:
                                colorScheme === "dark"
                                    ? theme.colors.dark[7]
                                    : theme.colors.gray[1],
                            cursor: isDragging ? "grabbing" : "grab",
                            touchAction: "none",
                            userSelect: "none",
                        })}
                    >
                        <ReferenceImage
                            reference={reference}
                            style={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain",
                                transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
                                transformOrigin: "center center",
                                transition: isDragging ? "none" : "transform 80ms ease-out",
                                pointerEvents: "none",
                            }}
                        />
                    </Box>
                </>
            )}
        </Modal>
    );
};

/** Thumbnail card styled like `ReferenceTooltip`, with the title underneath the image. */
export const ImageReferenceCard = ({
    reference,
    onOpen,
}: {
    reference: Reference;
    onOpen: (reference: Reference) => void;
}) => {
    const { colorScheme } = useMantineColorScheme();

    return (
        <UnstyledButton
            data-testid={`${reference.reference_id}-image`}
            onClick={() => onOpen(reference)}
            title={reference.title}
            style={(theme) => ({
                // Explicit so the card stays readable inside a Tooltip (which forces white text).
                color: colorScheme === "dark" ? theme.colors.dark[0] : theme.black,
                padding: theme.spacing.sm,
                borderRadius: theme.radius.sm,
                backgroundColor:
                    colorScheme === "dark" ? theme.colors.dark[6] : theme.colors.gray[0],
                height: "100%",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                border: `1px solid ${colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[2]}`,
                cursor: "pointer",
                transition: "background-color 150ms ease, box-shadow 150ms ease",
            })}
        >
            <Box
                style={(theme) => ({
                    height: 140,
                    borderRadius: theme.radius.sm,
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor:
                        colorScheme === "dark" ? theme.colors.dark[7] : theme.colors.gray[1],
                })}
            >
                <ReferenceImage
                    reference={reference}
                    lazy
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
            </Box>

            <Text
                size="xs"
                fw={500}
                mt="xs"
                lineClamp={2}
                style={{ lineHeight: 1.5, marginBottom: "auto" }}
            >
                {reference.title || "Untitled Image"}
            </Text>

            <Box
                style={(theme) => ({
                    marginTop: theme.spacing.xs,
                    paddingTop: theme.spacing.xs,
                    borderTop: `1px solid ${colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[2]}`,
                    display: "flex",
                    justifyContent: "flex-end",
                })}
            >
                <Text
                    style={(theme) => ({
                        padding: "2px 6px",
                        borderRadius: theme.radius.sm,
                        backgroundColor:
                            colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[2],
                        color: colorScheme === "dark" ? theme.colors.dark[0] : theme.colors.gray[7],
                        fontSize: "10px",
                        letterSpacing: "0.5px",
                        lineHeight: 1.5,
                    })}
                >
                    {getImageFormatLabel(reference)}
                </Text>
            </Box>
        </UnstyledButton>
    );
};

export const ImageReferenceGrid = ({ references }: { references: Reference[] }) => {
    const [selected, setSelected] = useState<Reference | null>(null);
    // Kept separate from `selected` so the image stays rendered during the close transition.
    const [opened, setOpened] = useState(false);
    const open = (reference: Reference) => {
        setSelected(reference);
        setOpened(true);
    };

    if (!references.length) {
        return (
            <Box p="md" ta="center">
                <Text size="sm" c="dimmed">
                    No images available for this answer.
                </Text>
            </Box>
        );
    }

    return (
        <>
            <Box
                data-testid="image-reference-grid"
                style={(theme) => ({
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                    gap: theme.spacing.md,
                })}
            >
                {references.map((reference) => (
                    <ImageReferenceCard
                        key={reference.reference_id}
                        reference={reference}
                        onOpen={open}
                    />
                ))}
            </Box>
            <ImageViewerModal
                reference={selected}
                opened={opened}
                onClose={() => setOpened(false)}
            />
        </>
    );
};
